import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, Building, Phone, Mail, Globe, MapPin, Mic, Users, Plus, Pencil, Trash2, X, UserCheck, UserX, Clock, Key, Copy, Check, Bell, Calendar, ExternalLink, Github } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { settings, users, apiKeys, calendar, holidays as holidaysApi, type Agency, type User, type BusinessHours, type NotificationPreferences, type Holiday } from '@/lib/api';
import { LinksWidget } from '@/components/LinksWidget';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.prima.myx.is';

// Day configuration type
interface DayConfig {
  enabled: boolean;
  open: string;
  close: string;
}

type DayKey = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

// Icelandic day names
const DAYS: { key: DayKey; name: string }[] = [
  { key: 'monday', name: 'Mánudagur' },
  { key: 'tuesday', name: 'Þriðjudagur' },
  { key: 'wednesday', name: 'Miðvikudagur' },
  { key: 'thursday', name: 'Fimmtudagur' },
  { key: 'friday', name: 'Föstudagur' },
  { key: 'saturday', name: 'Laugardagur' },
  { key: 'sunday', name: 'Sunnudagur' },
];

// Default business hours (weekdays 9-17, weekends closed)
const DEFAULT_HOURS: Record<DayKey, DayConfig> = {
  monday: { enabled: true, open: '09:00', close: '17:00' },
  tuesday: { enabled: true, open: '09:00', close: '17:00' },
  wednesday: { enabled: true, open: '09:00', close: '17:00' },
  thursday: { enabled: true, open: '09:00', close: '17:00' },
  friday: { enabled: true, open: '09:00', close: '17:00' },
  saturday: { enabled: false, open: '10:00', close: '14:00' },
  sunday: { enabled: false, open: '10:00', close: '14:00' },
};

// Default notification preferences
const DEFAULT_NOTIFICATIONS: NotificationPreferences = {
  email_notifications: true,
  sms_notifications: false,
  notify_new_leads: true,
  notify_viewing_reminders: true,
  notify_call_summaries: false,
};

// Role configuration with Icelandic labels and badge colors
type UserRole = 'admin' | 'manager' | 'agent';

const ROLE_CONFIG: Record<UserRole, { label: string; badgeClass: string }> = {
  admin: { label: 'Stjórnandi', badgeClass: 'bg-purple-100 text-purple-800' },
  manager: { label: 'Umsjónarmaður', badgeClass: 'bg-blue-100 text-blue-800' },
  agent: { label: 'Sölumaður', badgeClass: 'bg-gray-100 text-gray-800' },
};

// Helper function to get role label
const getRoleLabel = (role: string): string => {
  return ROLE_CONFIG[role as UserRole]?.label || role;
};

// Helper function to get role badge class
const getRoleBadgeClass = (role: string): string => {
  return ROLE_CONFIG[role as UserRole]?.badgeClass || 'bg-gray-100 text-gray-800';
};

// Helper function to format last login time
const formatLastLogin = (dateStr?: string): string => {
  if (!dateStr) return 'Aldrei';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  if (diffHours < 1) {
    return 'Fyrir minna en klst.';
  } else if (diffHours < 24) {
    return `Fyrir ${Math.floor(diffHours)} klst.`;
  } else if (diffDays < 7) {
    return `Fyrir ${Math.floor(diffDays)} dögum`;
  } else {
    return date.toLocaleDateString('is-IS', { day: 'numeric', month: 'short', year: 'numeric' });
  }
};

// Calendar Integration Component
function CalendarIntegration() {
  const { token } = useAuthStore();
  const [isConnecting, setIsConnecting] = useState(false);
  const queryClient = useQueryClient();

  // Fetch calendar status
  const { data: calendarStatus, isLoading } = useQuery({
    queryKey: ['calendar-status'],
    queryFn: () => calendar.status(token!),
    enabled: !!token,
  });

  // Disconnect mutation
  const disconnectMutation = useMutation({
    mutationFn: () => calendar.disconnect(token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-status'] });
    },
  });

  // Handle Connect button click
  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      const response = await calendar.getAuthUrl(token!);
      // Open OAuth URL in popup window
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      const authWindow = window.open(
        response.authUrl,
        'Google Calendar Authorization',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      // Poll for window close (indicates OAuth flow complete)
      const pollTimer = setInterval(() => {
        if (authWindow?.closed) {
          clearInterval(pollTimer);
          setIsConnecting(false);
          // Refresh calendar status after OAuth completes
          queryClient.invalidateQueries({ queryKey: ['calendar-status'] });
        }
      }, 500);
    } catch (error) {
      console.error('Failed to initiate OAuth:', error);
      setIsConnecting(false);
    }
  };

  // Handle Disconnect button click
  const handleDisconnect = () => {
    if (confirm('Ertu viss um að þú viljir aftengja Google Calendar?')) {
      disconnectMutation.mutate();
    }
  };

  // Format connection date
  const formatConnectedDate = (timestamp?: number) => {
    if (!timestamp) return '';
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('is-IS', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="card p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Calendar className="w-5 h-5 text-gray-400" />
        Google Calendar Samþætting
      </h2>

      {isLoading ? (
        <div className="text-sm text-gray-500">Hleður...</div>
      ) : calendarStatus?.connected ? (
        <div className="space-y-4">
          <div className="flex items-start justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Check className="w-5 h-5 text-green-600" />
                <p className="font-medium text-green-900">Tengt við Google Calendar</p>
              </div>
              <p className="text-sm text-green-700 ml-7">
                {calendarStatus.email}
              </p>
              {calendarStatus.connectedAt && (
                <p className="text-xs text-green-600 ml-7 mt-1">
                  Tengt: {formatConnectedDate(calendarStatus.connectedAt)}
                </p>
              )}
              {calendarStatus.tokenExpired && (
                <p className="text-xs text-orange-600 ml-7 mt-1 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Token útrunnið - endurtengist sjálfkrafa
                </p>
              )}
            </div>
            <button
              onClick={handleDisconnect}
              disabled={disconnectMutation.isPending}
              className="btn-outline text-sm text-red-600 border-red-300 hover:bg-red-50"
            >
              {disconnectMutation.isPending ? 'Aftengir...' : 'Aftengja'}
            </button>
          </div>

          <div className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="font-medium text-blue-900 mb-2">Hvað gerist nú?</p>
            <ul className="space-y-1 text-blue-800 ml-4">
              <li>• Skoðanir sem þú býrð til munu sjálfkrafa birtast í Google Calendar</li>
              <li>• Breytingar á skoðunum uppfærast í báðum kerfum</li>
              <li>• Þú getur séð tímabilið þitt beint í Prima</li>
            </ul>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Tengdu Google Calendar þinn til að samstilla skoðanir sjálfkrafa.
            Allar skoðanir sem þú býrð til í Prima munu birtast í Google Calendar þínum.
          </p>

          <div className="space-y-2 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="font-medium text-gray-900">Kostir:</p>
            <ul className="space-y-1 ml-4">
              <li>• ✅ Sjálfvirk samstilling við Google Calendar</li>
              <li>• ✅ Breytingar uppfærast í báðum kerfum</li>
              <li>• ✅ Sjá tímabilið þitt í einu yfirliti</li>
              <li>• ✅ Engar handvirkar uppfærslur</li>
            </ul>
          </div>

          <button
            onClick={handleConnect}
            disabled={isConnecting}
            className="btn-primary flex items-center gap-2"
          >
            {isConnecting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Tengir...
              </>
            ) : (
              <>
                <Calendar className="w-4 h-4" />
                Tengja Google Calendar
                <ExternalLink className="w-3 h-3" />
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

// GitHub Integration Component
function GitHubIntegration() {
  const { token } = useAuthStore();
  const [githubToken, setGithubToken] = useState('');
  const [githubRepo, setGithubRepo] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const queryClient = useQueryClient();

  // Fetch GitHub configuration
  const { data: githubConfig, isLoading } = useQuery({
    queryKey: ['github-config'],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/api/issues/config`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch GitHub config');
      }

      return response.json();
    },
    enabled: !!token,
  });

  // Initialize form with current config
  useEffect(() => {
    if (githubConfig && !isEditing) {
      setGithubRepo(githubConfig.github_repo || '');
    }
  }, [githubConfig, isEditing]);

  // Update GitHub configuration mutation
  const updateConfigMutation = useMutation({
    mutationFn: async (data: { github_token?: string; github_repo?: string }) => {
      const response = await fetch(`${API_URL}/api/issues/config`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update configuration');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['github-config'] });
      setIsEditing(false);
      setGithubToken(''); // Clear token input after save
    },
  });

  // Test token mutation
  const testTokenMutation = useMutation({
    mutationFn: async (token: string) => {
      const response = await fetch(`${API_URL}/api/issues/validate-token`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${await fetch('/api/auth/me').then(r => r.headers.get('Authorization'))}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Invalid token');
      }

      return response.json();
    },
  });

  const handleSave = () => {
    const data: { github_token?: string; github_repo?: string } = {};

    if (githubToken.trim()) {
      data.github_token = githubToken.trim();
    }

    if (githubRepo.trim()) {
      data.github_repo = githubRepo.trim();
    }

    if (Object.keys(data).length > 0) {
      updateConfigMutation.mutate(data);
    }
  };

  const handleTest = async () => {
    if (!githubToken.trim()) {
      return;
    }

    setIsTesting(true);
    try {
      await testTokenMutation.mutateAsync(githubToken.trim());
      alert('✅ Token is valid!');
    } catch (error) {
      alert(`❌ Invalid token: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsTesting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="card p-6">
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="card p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Github className="w-5 h-5 text-gray-400" />
        GitHub Samþætting
      </h2>

      {githubConfig?.configured && !isEditing ? (
        <div className="space-y-4">
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
            <Check className="w-5 h-5 text-green-600" />
            <div>
              <p className="text-sm font-medium text-green-900">Tengt við GitHub</p>
              <p className="text-sm text-green-700">Repository: {githubConfig.github_repo}</p>
            </div>
          </div>

          <div className="space-y-2 text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="font-medium text-blue-900">Virkt:</p>
            <ul className="space-y-1 text-blue-800 ml-4">
              <li>• Búa til GitHub issues beint úr stjórnborðinu</li>
              <li>• Fylgja eftir vandamálum og beiðnum</li>
              <li>• Sjálfvirk tenging við GitHub repository</li>
            </ul>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setIsEditing(true)}
              className="btn-secondary flex items-center gap-2"
            >
              <Pencil className="w-4 h-4" />
              Breyta uppsetningu
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Tengdu GitHub til að búa til issues beint úr stjórnborðinu. Þú þarft GitHub Personal Access Token með 'repo' og 'issues' réttindum.
          </p>

          <div className="space-y-4">
            {/* GitHub Token */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                GitHub Personal Access Token
              </label>
              <input
                type="password"
                value={githubToken}
                onChange={(e) => setGithubToken(e.target.value)}
                placeholder="ghp_xxxxxxxxxxxx"
                className="input w-full"
              />
              <p className="text-xs text-gray-500">
                Búðu til fine-grained PAT með 'Contents' og 'Issues' write aðgangi:{' '}
                <a
                  href="https://github.com/settings/tokens?type=beta"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  GitHub Settings → Tokens
                </a>
              </p>
              {githubToken.trim() && (
                <button
                  onClick={handleTest}
                  disabled={isTesting}
                  className="btn-secondary text-sm flex items-center gap-2"
                >
                  {isTesting ? 'Prufa...' : 'Prófa token'}
                </button>
              )}
            </div>

            {/* GitHub Repository */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                GitHub Repository
              </label>
              <input
                type="text"
                value={githubRepo}
                onChange={(e) => setGithubRepo(e.target.value)}
                placeholder="owner/repository"
                className="input w-full"
              />
              <p className="text-xs text-gray-500">
                Sláðu inn repository í forminu "eigandi/repository" (t.d. "2076ehf/prima.myx.is")
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            {isEditing && (
              <button
                onClick={() => {
                  setIsEditing(false);
                  setGithubToken('');
                  setGithubRepo(githubConfig?.github_repo || '');
                }}
                className="btn-secondary"
              >
                Hætta við
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={updateConfigMutation.isPending || (!githubToken.trim() && !githubRepo.trim())}
              className="btn-primary flex items-center gap-2"
            >
              {updateConfigMutation.isPending ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Vista...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Vista uppsetningu
                </>
              )}
            </button>
          </div>

          {updateConfigMutation.isError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800">
              ❌ {updateConfigMutation.error instanceof Error ? updateConfigMutation.error.message : 'Villa við að vista'}
            </div>
          )}

          {updateConfigMutation.isSuccess && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-800">
              ✅ Uppsetning vistuð!
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Holidays Management Component
function HolidaysSection({ isAdmin }: { isAdmin: boolean }) {
  const { token } = useAuthStore();
  const queryClient = useQueryClient();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showAddHoliday, setShowAddHoliday] = useState(false);
  const [newHoliday, setNewHoliday] = useState({ date: '', name: '', type: 'closed' as string, close_at: '' });

  const { data: holidaysData, isLoading } = useQuery({
    queryKey: ['holidays', selectedYear],
    queryFn: () => holidaysApi.list(token!, { year: selectedYear }),
    enabled: !!token,
  });

  const seedMutation = useMutation({
    mutationFn: () => holidaysApi.seed(token!, selectedYear),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['holidays', selectedYear] });
      alert(`${data.count} íslenskir frídagar bættust við!`);
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: { date: string; name: string; type?: string; close_at?: string }) =>
      holidaysApi.create(token!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['holidays', selectedYear] });
      setShowAddHoliday(false);
      setNewHoliday({ date: '', name: '', type: 'closed', close_at: '' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => holidaysApi.delete(token!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['holidays', selectedYear] });
    },
  });

  const handleAddHoliday = () => {
    if (!newHoliday.date || !newHoliday.name) return;
    createMutation.mutate({
      date: newHoliday.date,
      name: newHoliday.name,
      type: newHoliday.type,
      close_at: newHoliday.type === 'half_day' ? newHoliday.close_at || '12:00' : undefined,
    });
  };

  const holidaysList = holidaysData?.data || [];
  const currentYear = new Date().getFullYear();

  const typeLabel = (type: string) => {
    switch (type) {
      case 'closed': return 'Lokað';
      case 'half_day': return 'Hálf frí';
      case 'observance': return 'Merktir dagar';
      default: return type;
    }
  };

  const typeBadgeClass = (type: string) => {
    switch (type) {
      case 'closed': return 'bg-orange-100 text-orange-800';
      case 'half_day': return 'bg-amber-100 text-amber-800';
      case 'observance': return 'bg-gray-100 text-gray-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-gray-400" />
          Frídagar
        </h2>
        <div className="flex items-center gap-2">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="input py-1.5 px-3 text-sm w-24"
          >
            {[currentYear - 1, currentYear, currentYear + 1, currentYear + 2].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          {isAdmin && (
            <>
              <button
                onClick={() => seedMutation.mutate()}
                disabled={seedMutation.isPending}
                className="btn-secondary text-sm"
              >
                {seedMutation.isPending ? 'Hleður...' : 'Fylla inn almanaksdaga'}
              </button>
              <button
                onClick={() => setShowAddHoliday(true)}
                className="btn-primary text-sm"
              >
                <Plus className="w-4 h-4 mr-1" />
                Bæta við
              </button>
            </>
          )}
        </div>
      </div>

      <p className="text-sm text-gray-500 mb-4">
        Frídagar hafa áhrif á dagatal söluaðila og AI símsvörun. Á frídögum svarar AI öðruvísi og upplýsir um lokunardag.
      </p>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-prima-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : holidaysList.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>Engir frídagar skráðir fyrir {selectedYear}.</p>
          {isAdmin && (
            <p className="text-sm mt-2">
              Smelltu á „Fylla inn almanaksdaga" til að bæta við öllum íslenskum frídögum.
            </p>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Dagsetning</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Nafn</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Tegund</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Upplýsingar</th>
                {isAdmin && (
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Aðgerðir</th>
                )}
              </tr>
            </thead>
            <tbody>
              {holidaysList.map((h: Holiday) => {
                const dateObj = new Date(h.date + 'T12:00:00');
                const dayName = dateObj.toLocaleDateString('is-IS', { weekday: 'long' });
                return (
                  <tr key={h.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium text-gray-900">
                          {dateObj.toLocaleDateString('is-IS', { day: 'numeric', month: 'long' })}
                        </p>
                        <p className="text-xs text-gray-500 capitalize">{dayName}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4 font-medium text-gray-900">{h.name}</td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeBadgeClass(h.type)}`}>
                        {typeLabel(h.type)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-500">
                      {h.type === 'half_day' && h.close_at && `Lokað kl. ${h.close_at}`}
                      {h.is_national && (
                        <span className="text-xs text-blue-600 ml-1">Almanaksdagur</span>
                      )}
                    </td>
                    {isAdmin && (
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={() => {
                            if (confirm(`Eyða ${h.name}?`)) {
                              deleteMutation.mutate(h.id);
                            }
                          }}
                          disabled={deleteMutation.isPending}
                          className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                          title="Eyða"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Holiday Modal */}
      {showAddHoliday && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Bæta við frídegi</h3>
              <button
                onClick={() => setShowAddHoliday(false)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="label">Dagsetning</label>
                <input
                  type="date"
                  value={newHoliday.date}
                  onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })}
                  className="input"
                />
              </div>
              <div>
                <label className="label">Nafn</label>
                <input
                  type="text"
                  value={newHoliday.name}
                  onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })}
                  className="input"
                  placeholder="t.d. Fundardagur fyrirtækis"
                />
              </div>
              <div>
                <label className="label">Tegund</label>
                <select
                  value={newHoliday.type}
                  onChange={(e) => setNewHoliday({ ...newHoliday, type: e.target.value })}
                  className="input"
                >
                  <option value="closed">Lokað allan daginn</option>
                  <option value="half_day">Hálf frí (lokað eftir tíma)</option>
                  <option value="observance">Merktir dagar (opið eðlilega)</option>
                </select>
              </div>
              {newHoliday.type === 'half_day' && (
                <div>
                  <label className="label">Lokað eftir</label>
                  <input
                    type="time"
                    value={newHoliday.close_at || '12:00'}
                    onChange={(e) => setNewHoliday({ ...newHoliday, close_at: e.target.value })}
                    className="input"
                  />
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 p-4 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setShowAddHoliday(false)}
                className="btn-secondary"
              >
                Hætta við
              </button>
              <button
                onClick={handleAddHoliday}
                disabled={!newHoliday.date || !newHoliday.name || createMutation.isPending}
                className="btn-primary"
              >
                {createMutation.isPending ? 'Vista...' : 'Bæta við'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Settings() {
  const { token, user } = useAuthStore();
  const queryClient = useQueryClient();

  const { data: agency, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => settings.get(token!),
    enabled: !!token,
  });

  const { data: usersList } = useQuery({
    queryKey: ['users'],
    queryFn: () => users.list(token!),
    enabled: !!token,
  });

  const { data: apiKeysData } = useQuery({
    queryKey: ['apiKeys'],
    queryFn: () => apiKeys.get(token!),
    enabled: !!token && user?.role === 'admin',
  });

  const [formData, setFormData] = useState<Partial<Agency>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [newUser, setNewUser] = useState({ email: '', name: '', role: 'agent' });
  const [businessHours, setBusinessHours] = useState<Record<DayKey, DayConfig>>(DEFAULT_HOURS);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [notificationPreferences, setNotificationPreferences] = useState<NotificationPreferences>(DEFAULT_NOTIFICATIONS);

  // Initialize business hours from agency data
  useEffect(() => {
    if (agency?.business_hours) {
      const hours = agency.business_hours;
      const newHours: Record<DayKey, DayConfig> = { ...DEFAULT_HOURS };

      for (const day of DAYS) {
        const dayData = hours[day.key];
        if (dayData) {
          newHours[day.key] = {
            enabled: true,
            open: dayData.open,
            close: dayData.close,
          };
        } else {
          newHours[day.key] = {
            ...DEFAULT_HOURS[day.key],
            enabled: false,
          };
        }
      }

      setBusinessHours(newHours);
    }
  }, [agency?.business_hours]);

  // Initialize notification preferences from agency data
  useEffect(() => {
    if (agency?.notification_preferences) {
      setNotificationPreferences({
        ...DEFAULT_NOTIFICATIONS,
        ...agency.notification_preferences,
      });
    }
  }, [agency?.notification_preferences]);

  const updateMutation = useMutation({
    mutationFn: (data: Partial<Agency>) => settings.update(token!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setHasChanges(false);
      setFormData({});
    },
  });

  const createUserMutation = useMutation({
    mutationFn: (data: { email: string; name: string; role?: string }) =>
      users.create(token!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowAddUser(false);
      setNewUser({ email: '', name: '', role: 'agent' });
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<{ name: string; role: string; active: boolean }> }) =>
      users.update(token!, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setEditingUser(null);
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (id: string) => users.delete(token!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const handleChange = (field: keyof Agency, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleBusinessHoursChange = (day: DayKey, field: 'enabled' | 'open' | 'close', value: boolean | string) => {
    setBusinessHours((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value,
      },
    }));
    setHasChanges(true);
  };

  const handleNotificationChange = (field: keyof NotificationPreferences, value: boolean) => {
    setNotificationPreferences((prev) => ({
      ...prev,
      [field]: value,
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    // Convert business hours to API format (only include enabled days)
    const businessHoursData: BusinessHours = {};
    for (const day of DAYS) {
      const dayConfig = businessHours[day.key];
      if (dayConfig.enabled) {
        businessHoursData[day.key] = {
          open: dayConfig.open,
          close: dayConfig.close,
        };
      }
    }

    const dataToSave = {
      ...formData,
      business_hours: businessHoursData,
      notification_preferences: notificationPreferences,
    };

    updateMutation.mutate(dataToSave);
  };

  const handleAddUser = () => {
    if (newUser.email && newUser.name) {
      createUserMutation.mutate(newUser);
    }
  };

  const handleToggleActive = (u: User) => {
    updateUserMutation.mutate({ id: u.id, data: { active: !u.active } });
  };

  const handleDeleteUser = (id: string) => {
    if (confirm('Ertu viss um að þú viljir eyða þessum notanda?')) {
      deleteUserMutation.mutate(id);
    }
  };

  // Helper to mask API keys showing only last 4 characters
  const maskKey = (key: string | null | undefined): string => {
    if (!key) return '';
    if (key.length <= 4) return key;
    return '••••••••' + key.slice(-4);
  };

  // Copy to clipboard with feedback
  const copyToClipboard = async (key: string, keyName: string) => {
    try {
      await navigator.clipboard.writeText(key);
      setCopiedKey(keyName);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch {
      console.error('Failed to copy to clipboard');
    }
  };

  const currentValues = { ...agency, ...formData };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-prima-600" />
      </div>
    );
  }

  const isAdmin = user?.role === 'admin';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stillingar</h1>
          <p className="text-gray-600 mt-1">
            Stillingar fyrir {agency?.name}
          </p>
        </div>
        {hasChanges && isAdmin && (
          <button
            onClick={handleSave}
            disabled={updateMutation.isPending}
            className="btn-primary"
          >
            <Save className="w-4 h-4 mr-2" />
            {updateMutation.isPending ? 'Vista...' : 'Vista breytingar'}
          </button>
        )}
      </div>

      {!isAdmin && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800 text-sm">
            Þú hefur ekki leyfi til að breyta stillingum. Hafðu samband við stjórnanda.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Company info */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Building className="w-5 h-5 text-gray-400" />
            Fyrirtækisupplýsingar
          </h2>
          <div className="space-y-4">
            <div>
              <label className="label">Nafn fyrirtækis</label>
              <input
                type="text"
                value={currentValues?.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
                disabled={!isAdmin}
                className="input"
              />
            </div>
            <div>
              <label className="label">Sími</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="tel"
                  value={currentValues?.phone || ''}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  disabled={!isAdmin}
                  className="input pl-10"
                  placeholder="+354 555 1234"
                />
              </div>
            </div>
            <div>
              <label className="label">Netfang</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="email"
                  value={currentValues?.email || ''}
                  onChange={(e) => handleChange('email', e.target.value)}
                  disabled={!isAdmin}
                  className="input pl-10"
                  placeholder="info@primafasteignir.is"
                />
              </div>
            </div>
            <div>
              <label className="label">Vefsíða</label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="url"
                  value={currentValues?.website || ''}
                  onChange={(e) => handleChange('website', e.target.value)}
                  disabled={!isAdmin}
                  className="input pl-10"
                  placeholder="https://primafasteignir.is"
                />
              </div>
            </div>
            <div>
              <label className="label">Heimilisfang</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={currentValues?.address || ''}
                  onChange={(e) => handleChange('address', e.target.value)}
                  disabled={!isAdmin}
                  className="input pl-10"
                  placeholder="Laugavegur 77, 101 Reykjavík"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Voice AI settings */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Mic className="w-5 h-5 text-gray-400" />
            Raddar AI stillingar
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">AI símsvörun</p>
                <p className="text-sm text-gray-500">
                  Kveikja á sjálfvirkri símsvörun
                </p>
              </div>
              <button
                onClick={() => handleChange('voice_enabled', !currentValues?.voice_enabled)}
                disabled={!isAdmin}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  currentValues?.voice_enabled ? 'bg-prima-600' : 'bg-gray-200'
                } ${!isAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    currentValues?.voice_enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div>
              <label className="label">Kveðja</label>
              <textarea
                value={currentValues?.voice_greeting || ''}
                onChange={(e) => handleChange('voice_greeting', e.target.value)}
                disabled={!isAdmin}
                rows={4}
                className="input"
                placeholder="Góðan daginn og velkomin í Prima Fasteignir. Hvernig get ég aðstoðað þig?"
              />
              <p className="text-xs text-gray-500 mt-1">
                Þetta er texti sem AI notar til að kveðja viðskiptavini
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Opening Hours */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-gray-400" />
          Opnunartímar
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          Stilltu opnunartíma fyrirtækisins. AI mun nota þessar upplýsingar til að svara spurningum um opnunartíma.
        </p>
        <div className="space-y-3">
          {DAYS.map(({ key, name }) => (
            <div key={key} className="flex items-center gap-4 py-2 border-b border-gray-100 last:border-0">
              <label className="flex items-center gap-2 w-36">
                <input
                  type="checkbox"
                  checked={businessHours[key].enabled}
                  onChange={(e) => handleBusinessHoursChange(key, 'enabled', e.target.checked)}
                  disabled={!isAdmin}
                  className="w-4 h-4 rounded border-gray-300 text-prima-600 focus:ring-prima-500 disabled:opacity-50"
                />
                <span className="text-sm font-medium text-gray-700">{name}</span>
              </label>
              {businessHours[key].enabled ? (
                <div className="flex items-center gap-2">
                  <input
                    type="time"
                    value={businessHours[key].open}
                    onChange={(e) => handleBusinessHoursChange(key, 'open', e.target.value)}
                    disabled={!isAdmin}
                    className="input py-1.5 px-2 w-28 text-sm"
                  />
                  <span className="text-gray-400">-</span>
                  <input
                    type="time"
                    value={businessHours[key].close}
                    onChange={(e) => handleBusinessHoursChange(key, 'close', e.target.value)}
                    disabled={!isAdmin}
                    className="input py-1.5 px-2 w-28 text-sm"
                  />
                </div>
              ) : (
                <span className="text-sm text-gray-400 italic">Lokað</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Holidays */}
      <HolidaysSection isAdmin={isAdmin} />

      {/* Notification Preferences */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Bell className="w-5 h-5 text-gray-400" />
          Tilkynningar
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          Stilltu hvaða tilkynningar þú vilt fá frá kerfinu.
        </p>
        <div className="space-y-4">
          {/* Email notifications */}
          <div className="flex items-center justify-between py-3 border-b border-gray-100">
            <div>
              <p className="font-medium text-gray-900">Tilkynningar í tölvupósti</p>
              <p className="text-sm text-gray-500">Fá tilkynningar sendar á netfangið þitt</p>
            </div>
            <button
              onClick={() => handleNotificationChange('email_notifications', !notificationPreferences.email_notifications)}
              disabled={!isAdmin}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                notificationPreferences.email_notifications ? 'bg-prima-600' : 'bg-gray-200'
              } ${!isAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  notificationPreferences.email_notifications ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* SMS notifications */}
          <div className="flex items-center justify-between py-3 border-b border-gray-100">
            <div>
              <p className="font-medium text-gray-900">SMS tilkynningar</p>
              <p className="text-sm text-gray-500">Fá tilkynningar sendar í síma</p>
            </div>
            <button
              onClick={() => handleNotificationChange('sms_notifications', !notificationPreferences.sms_notifications)}
              disabled={!isAdmin}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                notificationPreferences.sms_notifications ? 'bg-prima-600' : 'bg-gray-200'
              } ${!isAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  notificationPreferences.sms_notifications ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* New lead notifications */}
          <div className="flex items-center justify-between py-3 border-b border-gray-100">
            <div>
              <p className="font-medium text-gray-900">Nýjar ábendingar</p>
              <p className="text-sm text-gray-500">Fá tilkynningu um nýjar ábendingar frá símtölum</p>
            </div>
            <button
              onClick={() => handleNotificationChange('notify_new_leads', !notificationPreferences.notify_new_leads)}
              disabled={!isAdmin}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                notificationPreferences.notify_new_leads ? 'bg-prima-600' : 'bg-gray-200'
              } ${!isAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  notificationPreferences.notify_new_leads ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Viewing reminder notifications */}
          <div className="flex items-center justify-between py-3 border-b border-gray-100">
            <div>
              <p className="font-medium text-gray-900">Áminningar um skoðanir</p>
              <p className="text-sm text-gray-500">Fá áminningu fyrir bókaðar eignaskoðanir</p>
            </div>
            <button
              onClick={() => handleNotificationChange('notify_viewing_reminders', !notificationPreferences.notify_viewing_reminders)}
              disabled={!isAdmin}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                notificationPreferences.notify_viewing_reminders ? 'bg-prima-600' : 'bg-gray-200'
              } ${!isAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  notificationPreferences.notify_viewing_reminders ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Call summary notifications */}
          <div className="flex items-center justify-between py-3">
            <div>
              <p className="font-medium text-gray-900">Samantekt símtala</p>
              <p className="text-sm text-gray-500">Fá daglega samantekt af símtölum og AI svörun</p>
            </div>
            <button
              onClick={() => handleNotificationChange('notify_call_summaries', !notificationPreferences.notify_call_summaries)}
              disabled={!isAdmin}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                notificationPreferences.notify_call_summaries ? 'bg-prima-600' : 'bg-gray-200'
              } ${!isAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  notificationPreferences.notify_call_summaries ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Links Widget - Quick Links */}
      <div className="lg:row-span-2">
        <LinksWidget />
      </div>

      {/* API Keys - Admin only */}
      {isAdmin && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Key className="w-5 h-5 text-gray-400" />
            API Lyklar
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            API lyklar fyrir samþættingar. Hafðu samband við stuðning til að uppfæra lykla.
          </p>
          <div className="space-y-3">
            {/* Twilio Account SID */}
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div className="flex-1">
                <p className="font-medium text-gray-900">Twilio Account SID</p>
                <p className="text-sm text-gray-500">Símtal og SMS þjónusta</p>
              </div>
              <div className="flex items-center gap-2">
                {apiKeysData?.twilio_account_sid ? (
                  <>
                    <code className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md text-sm font-mono">
                      {maskKey(apiKeysData.twilio_account_sid)}
                    </code>
                    <button
                      onClick={() => copyToClipboard(apiKeysData.twilio_account_sid!, 'twilio')}
                      className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors"
                      title="Afrita"
                    >
                      {copiedKey === 'twilio' ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                    <a
                      href="https://console.twilio.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-blue-600 hover:text-blue-700 rounded-md hover:bg-blue-50 transition-colors"
                      title="Opna Twilio Console"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </>
                ) : (
                  <span className="text-sm text-gray-400 italic">Ekki stillt</span>
                )}
              </div>
            </div>

            {/* Google Cloud Project ID */}
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div className="flex-1">
                <p className="font-medium text-gray-900">Google Cloud Project ID</p>
                <p className="text-sm text-gray-500">AI og tal þjónusta</p>
              </div>
              <div className="flex items-center gap-2">
                {apiKeysData?.google_project_id ? (
                  <>
                    <code className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md text-sm font-mono">
                      {apiKeysData.google_project_id}
                    </code>
                    <button
                      onClick={() => copyToClipboard(apiKeysData.google_project_id!, 'google')}
                      className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors"
                      title="Afrita"
                    >
                      {copiedKey === 'google' ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                    <a
                      href={`https://console.cloud.google.com/home/dashboard?project=${apiKeysData.google_project_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-blue-600 hover:text-blue-700 rounded-md hover:bg-blue-50 transition-colors"
                      title="Opna Google Cloud Console"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </>
                ) : (
                  <span className="text-sm text-gray-400 italic">Ekki stillt</span>
                )}
              </div>
            </div>

            {/* Dialogflow Agent ID */}
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div className="flex-1">
                <p className="font-medium text-gray-900">Dialogflow Agent ID</p>
                <p className="text-sm text-gray-500">Samtalsvélsstilling</p>
              </div>
              <div className="flex items-center gap-2">
                {apiKeysData?.dialogflow_agent_id && apiKeysData?.google_project_id && apiKeysData?.dialogflow_location ? (
                  <>
                    <code className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md text-sm font-mono">
                      {apiKeysData.dialogflow_agent_id.slice(0, 8)}...
                    </code>
                    <button
                      onClick={() => copyToClipboard(apiKeysData.dialogflow_agent_id!, 'dialogflow')}
                      className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors"
                      title="Afrita"
                    >
                      {copiedKey === 'dialogflow' ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                    <a
                      href={`https://dialogflow.cloud.google.com/cx/projects/${apiKeysData.google_project_id}/locations/${apiKeysData.dialogflow_location}/agents/${apiKeysData.dialogflow_agent_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-blue-600 hover:text-blue-700 rounded-md hover:bg-blue-50 transition-colors"
                      title="Opna Dialogflow CX Console"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </>
                ) : (
                  <span className="text-sm text-gray-400 italic">Ekki stillt</span>
                )}
              </div>
            </div>

            {/* Twilio Phone Number */}
            {apiKeysData?.twilio_phone_number && (
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Twilio Símanúmer</p>
                  <p className="text-sm text-gray-500">Símtöl tekið á þessu númeri</p>
                </div>
                <div className="flex items-center gap-2">
                  <code className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md text-sm font-mono">
                    {apiKeysData.twilio_phone_number}
                  </code>
                  <button
                    onClick={() => copyToClipboard(apiKeysData.twilio_phone_number!, 'twilio-phone')}
                    className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors"
                    title="Afrita"
                  >
                    {copiedKey === 'twilio-phone' ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                  <a
                    href="https://console.twilio.com/us1/develop/phone-numbers/manage/incoming"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-blue-600 hover:text-blue-700 rounded-md hover:bg-blue-50 transition-colors"
                    title="Opna Twilio Phone Numbers"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
            )}

            {/* Agency API Key */}
            <div className="flex items-center justify-between py-3">
              <div>
                <p className="font-medium text-gray-900">Agency API Lykill</p>
                <p className="text-sm text-gray-500">Webhook og ytri samþættingar</p>
              </div>
              <div className="flex items-center gap-2">
                {apiKeysData?.agency_api_key ? (
                  <>
                    <code className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md text-sm font-mono">
                      {maskKey(apiKeysData.agency_api_key)}
                    </code>
                    <button
                      onClick={() => copyToClipboard(apiKeysData.agency_api_key!, 'agency')}
                      className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors"
                      title="Afrita"
                    >
                      {copiedKey === 'agency' ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </>
                ) : (
                  <span className="text-sm text-gray-400 italic">Ekki stillt</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* GitHub Integration */}
      <GitHubIntegration />

      {/* Google Calendar Integration */}
      <CalendarIntegration />

      {/* User Management - Admin only */}
      {isAdmin && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-gray-400" />
              Notendur
            </h2>
            <button
              onClick={() => setShowAddUser(true)}
              className="btn-primary text-sm"
            >
              <Plus className="w-4 h-4 mr-1" />
              Bæta við notanda
            </button>
          </div>

          {/* Users list */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Notandi</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Netfang</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Hlutverk</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Staða</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Síðast innskráður</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Aðgerðir</th>
                </tr>
              </thead>
              <tbody>
                {usersList?.data.map((u) => (
                  <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        {u.picture ? (
                          <img src={u.picture} alt={u.name} className="w-8 h-8 rounded-full" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-prima-100 flex items-center justify-center">
                            <span className="text-prima-700 font-medium text-sm">
                              {u.name?.charAt(0) || 'U'}
                            </span>
                          </div>
                        )}
                        <span className="font-medium text-gray-900">{u.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-600">{u.email}</td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeClass(u.role)}`}>
                        {getRoleLabel(u.role)}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        u.active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {u.active ? (
                          <>
                            <UserCheck className="w-3 h-3" />
                            Virkur
                          </>
                        ) : (
                          <>
                            <UserX className="w-3 h-3" />
                            Óvirkur
                          </>
                        )}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-500">
                      {formatLastLogin(u.last_login_at)}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setEditingUser(u)}
                          className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                          title="Breyta"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleActive(u)}
                          className={`p-1.5 rounded ${
                            u.active
                              ? 'text-gray-400 hover:text-orange-600'
                              : 'text-gray-400 hover:text-green-600'
                          }`}
                          title={u.active ? 'Gera óvirkan' : 'Gera virkan'}
                        >
                          {u.active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                        </button>
                        {u.id !== user?.id && (
                          <button
                            onClick={() => handleDeleteUser(u.id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                            title="Eyða"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Account info */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Þinn aðgangur</h2>
        <div className="flex items-center gap-4">
          {user?.picture ? (
            <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full" />
          ) : (
            <div className="w-16 h-16 rounded-full bg-prima-100 flex items-center justify-center">
              <span className="text-prima-700 font-medium text-xl">
                {user?.name?.charAt(0) || 'U'}
              </span>
            </div>
          )}
          <div>
            <p className="font-medium text-gray-900">{user?.name}</p>
            <p className="text-sm text-gray-500">{user?.email}</p>
            <p className="text-sm text-gray-500">
              Hlutverk: {user?.role ? getRoleLabel(user.role) : 'Óþekkt'}
            </p>
          </div>
        </div>
      </div>

      {/* Add User Modal */}
      {showAddUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Bæta við notanda</h3>
              <button
                onClick={() => setShowAddUser(false)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="label">Nafn</label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  className="input"
                  placeholder="Jón Jónsson"
                />
              </div>
              <div>
                <label className="label">Netfang</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="input"
                  placeholder="jon@fyrirtaeki.is"
                />
              </div>
              <div>
                <label className="label">Hlutverk</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="input"
                >
                  <option value="agent">Sölumaður</option>
                  <option value="manager">Umsjónarmaður</option>
                  <option value="admin">Stjórnandi</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Stjórnandi hefur fullan aðgang, umsjónarmaður getur lesið/skrifað flest, sölumaður hefur takmarkaðan aðgang.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setShowAddUser(false)}
                className="btn-secondary"
              >
                Hætta við
              </button>
              <button
                onClick={handleAddUser}
                disabled={!newUser.email || !newUser.name || createUserMutation.isPending}
                className="btn-primary"
              >
                {createUserMutation.isPending ? 'Bý til...' : 'Bæta við'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Breyta notanda</h3>
              <button
                onClick={() => setEditingUser(null)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="label">Nafn</label>
                <input
                  type="text"
                  value={editingUser.name}
                  onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                  className="input"
                />
              </div>
              <div>
                <label className="label">Netfang</label>
                <input
                  type="email"
                  value={editingUser.email}
                  disabled
                  className="input bg-gray-50"
                />
                <p className="text-xs text-gray-500 mt-1">Netfang er ekki hægt að breyta</p>
              </div>
              <div>
                <label className="label">Hlutverk</label>
                <select
                  value={editingUser.role}
                  onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value as 'admin' | 'manager' | 'agent' })}
                  className="input"
                >
                  <option value="agent">Sölumaður</option>
                  <option value="manager">Umsjónarmaður</option>
                  <option value="admin">Stjórnandi</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Stjórnandi hefur fullan aðgang, umsjónarmaður getur lesið/skrifað flest, sölumaður hefur takmarkaðan aðgang.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setEditingUser(null)}
                className="btn-secondary"
              >
                Hætta við
              </button>
              <button
                onClick={() => updateUserMutation.mutate({
                  id: editingUser.id,
                  data: { name: editingUser.name, role: editingUser.role }
                })}
                disabled={updateUserMutation.isPending}
                className="btn-primary"
              >
                {updateUserMutation.isPending ? 'Vista...' : 'Vista'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
